import * as Api from '@ViewModels';
import { toast } from 'sonner';
import {
	IVolunteerShift,
	useArchiveVolunteerShiftMutation,
	useCreateVolunteerShiftMutation,
	useUpdateVolunteerShiftMutation,
	useVolunteerShiftsQuery,
} from '../../../queries/Volunteer';

export interface ShiftDataSource {
	shifts: IVolunteerShift[];
	isLoading: boolean;
	createShifts(
		bodies: Partial<IVolunteerShift>[]
	): Promise<{ created: number; failed: number; succeeded: Partial<IVolunteerShift>[] }>;
	updateShift(shift: IVolunteerShift): Promise<void>;
	archiveShift(shiftId: string): Promise<void>;
}

export function useServerShiftDataSource(
	activityId: string,
	impersonationContext?: Api.IImpersonationContext
): ShiftDataSource {
	const { data: shifts = [], isLoading } = useVolunteerShiftsQuery({ activityId, impersonationContext });
	const createMutation = useCreateVolunteerShiftMutation();
	const updateMutation = useUpdateVolunteerShiftMutation({
		onError: error => toast.error(error.systemMessage || 'Failed to save the shift'),
	});
	const archiveMutation = useArchiveVolunteerShiftMutation();

	return {
		shifts,
		isLoading,
		createShifts: async bodies => {
			const results = await Promise.allSettled(
				bodies.map(shift => createMutation.mutateAsync({ activityId, shift, impersonationContext }))
			);
			const succeeded = bodies.filter((_, index) => results[index].status === 'fulfilled');
			return { created: succeeded.length, failed: results.length - succeeded.length, succeeded };
		},
		updateShift: async shift => {
			await updateMutation.mutateAsync({ shift, impersonationContext });
		},
		archiveShift: async shiftId => {
			await archiveMutation.mutateAsync({ shiftId, activityId, impersonationContext });
		},
	};
}
